import { useCallback, useMemo, type PropsWithChildren } from 'react'
import {
  mutateAllUserNotifications,
  mutateUserNotifications,
  removeDigestItem as removeDigestItemMutation,
  restoreUserNotifications,
} from '../services/notificationService'
import { useNotificationListener } from '../hooks/useNotificationListener'
import { useNotificationPreferences } from '../hooks/useNotificationPreferences'
import type { NotificationRecord } from '../types/schema'
import { useAuth } from './useAuth'
import { useWorkspace } from './useWorkspace'
import { useGlobalActionLoading } from './useGlobalActionLoading'
import { switchedOffModulesFor } from '../utils/workspaceModules'
import { hideFunPackNotification } from '../utils/workspaceModulesNotifications'
import { hidePageNotification } from '../utils/workspacePageNotifications'
import { usePageVisibility } from '../services/pageVisibilityService'
import {
  NotificationContext,
  type NotificationQuickAction,
} from './notificationContextCore'

const dispatchNotificationEvent = (name: string, notification: NotificationRecord) => {
  window.dispatchEvent(new CustomEvent(name, { detail: { notification } }))
}

const hasLocalNotificationTarget = (notification: NotificationRecord) => {
  const deepLink = notification.deepLink ?? notification.deeplink
  if (deepLink && deepLink.startsWith('/') && deepLink !== '/') return true
  if (['GOAL', 'TASK', 'SUBTASK', 'CHECKLIST_ITEM'].includes(notification.entityType)) return true
  return notification.eventType === 'MENTION_CREATED' || notification.eventType === 'INVITE_RECEIVED'
}

const dispatchOpenAction = (action: NotificationQuickAction, notification: NotificationRecord) => {
  if (action === 'TOP_UP_CREDITS') {
    dispatchNotificationEvent('timelining:notification-top-up', notification)
    return
  }
  if (action === 'JOIN_RAID') {
    dispatchNotificationEvent('timelining:notification-join-raid', notification)
    return
  }
  if (action === 'OPEN_CHAT') {
    dispatchNotificationEvent('timelining:notification-open-chat', notification)
    return
  }
  if (action === 'REVIEW_INVITE') {
    dispatchNotificationEvent('timelining:notification-review-invite', notification)
    return
  }

  dispatchNotificationEvent('timelining:notification-open', notification)
}

export const NotificationProvider = ({ children }: PropsWithChildren) => {
  const { currentUser } = useAuth()
  const { workspaces = [], activeWorkspaceId } = useWorkspace()
  const { runWithGlobalLoading } = useGlobalActionLoading()
  const userId = currentUser?.uid ?? null
  const rawNotifications = useNotificationListener(userId)
  const { preferences, savePreferences } = useNotificationPreferences()

  const funPackOffWorkspaceIds = useMemo(() => {
    const set = new Set<string>()
    for (const ws of workspaces) {
      if (switchedOffModulesFor(ws).has('funPack')) {
        set.add(ws.id)
      }
    }
    return set
  }, [workspaces])

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId],
  )
  const activeWorkspaceFunPackOff = useMemo(
    () => switchedOffModulesFor(activeWorkspace).has('funPack'),
    [activeWorkspace],
  )

  const pageVisibility = usePageVisibility()

  const notifications = useMemo(() => {
    return rawNotifications
      .filter((n) => !hideFunPackNotification({
        eventType: n.eventType,
        category: n.category,
        workspaceId: n.workspaceId,
      }, {
        funPackOffWorkspaceIds,
        activeWorkspaceFunPackOff,
      }))
      .filter((n) => !hidePageNotification({
        eventType: n.eventType,
        category: n.category,
        workspaceId: n.workspaceId,
      }, {
        controls: pageVisibility.effectiveControls,
        isOwner: pageVisibility.isOwner,
        role: pageVisibility.role,
      }))
  }, [rawNotifications, funPackOffWorkspaceIds, activeWorkspaceFunPackOff, pageVisibility])


  const markRead = useCallback(async (notificationId: string) => {
    const target = notifications.find((entry) => entry.id === notificationId)
    if (!target || target.readAt) return
    await mutateUserNotifications('READ', [notificationId])
  }, [notifications])

  const markAllRead = useCallback(async () => {
    if (!notifications.some((entry) => !entry.readAt)) return
    await mutateAllUserNotifications('READ')
  }, [notifications])

  const clearNotification = useCallback(async (notificationId: string) => {
    const target = notifications.find((entry) => entry.id === notificationId)
    if (!target) return
    await mutateUserNotifications('CLEAR', [notificationId])
  }, [notifications])

  const clearAll = useCallback(async () => {
    if (notifications.length === 0) return
    await mutateAllUserNotifications('CLEAR')
  }, [notifications])

  const restoreNotifications = useCallback(async (notificationIds: string[]) => {
    if (notificationIds.length === 0) return
    await restoreUserNotifications(notificationIds)
  }, [])

  const removeDigestItem = useCallback(async (digestId: string, itemIndex: number) => {
    await removeDigestItemMutation(digestId, itemIndex)
  }, [])

  const openNotification = useCallback(async (
    notification: NotificationRecord,
    action: NotificationQuickAction = 'OPEN',
  ) => {
    await runWithGlobalLoading(async (signal) => {
      if (signal.aborted) return
      if (action === 'DISMISS') {
        await clearNotification(notification.id)
        return
      }

      const clickMutation = mutateUserNotifications('CLICK', [notification.id])
        .then(async (result) => {
          await mutateUserNotifications('CLEAR', [notification.id]).catch(() => undefined)
          return result
        })
        .catch(() => ({ updated: 0, deepLink: null }))

      if (hasLocalNotificationTarget(notification)) {
        dispatchOpenAction(action, notification)
        void clickMutation
        return
      }

      const result = await clickMutation
      if (signal.aborted) return
      const opened = result.deepLink ? { ...notification, deepLink: result.deepLink, deeplink: result.deepLink } : notification
      dispatchOpenAction(action, opened)
    }, {
      label: 'Opening notification',
      messages: [
        'Opening the right workspace screen...',
        'Marking the signal as handled...',
        'Syncing the notification queue...',
        'Keeping duplicate taps blocked...',
      ],
    })
  }, [clearNotification, runWithGlobalLoading])


  const value = useMemo(() => ({
    notifications,
    unreadCount: notifications.filter((notification) => !notification.readAt).length,
    preferences,
    openNotification,
    markRead,
    markAllRead,
    clearNotification,
    clearAll,
    restoreNotifications,
    removeDigestItem,
    savePreferences,
  }), [
    clearAll,
    clearNotification,
    markAllRead,
    markRead,
    notifications,
    openNotification,
    preferences,
    removeDigestItem,
    restoreNotifications,
    savePreferences,
  ])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}
